/**
 * Engine-specific node library — nodes that depend on the game engine bridge.
 * These are shipped with @engine/core and require an EngineBridge at runtime.
 */
import type { NodeBehaviour, CustomTypeDefinition } from '@engine/redprint';
import { NodeRegistry, getDefaultRegistry, STANDARD_NODES, registerTypeMeta } from '@engine/redprint';
import { componentRegistry as decoratorRegistry } from '../attributes/component';

export { LIFECYCLE_NODES } from './lifecycle';
export { TRANSFORM_NODES } from './transform';
export { GAMEOBJECT_NODES } from './gameobject';
export { INPUT_ADVANCED_NODES } from './input-advanced';
export { SPRITE_NODES } from './sprite';
export { PHYSICS_NODES } from './physics';
export { PHYSICS_ADVANCED_NODES } from './physics-advanced';
export { AUDIO_NODES } from './audio';
export { ZONES_NODES } from './zones';
export { LIGHTING_NODES } from './lighting';
export { CAMERA_NODES } from './camera';
export { VFX_NODES } from './vfx';
export { UTILITY_NODES } from './utility';
export { DEBUG_NODES } from './debug';

import { LIFECYCLE_NODES } from './lifecycle';
import { TRANSFORM_NODES } from './transform';
import { GAMEOBJECT_NODES } from './gameobject';
import { INPUT_ADVANCED_NODES } from './input-advanced';
import { SPRITE_NODES } from './sprite';
import { PHYSICS_NODES } from './physics';
import { PHYSICS_ADVANCED_NODES } from './physics-advanced';
import { AUDIO_NODES } from './audio';
import { ZONES_NODES } from './zones';
import { LIGHTING_NODES } from './lighting';
import { CAMERA_NODES } from './camera';
import { VFX_NODES } from './vfx';
import { UTILITY_NODES } from './utility';
import { DEBUG_NODES } from './debug';

/** All engine-specific nodes bundled with @engine/core. */
export const ENGINE_NODES: readonly NodeBehaviour[] = [
  ...LIFECYCLE_NODES,
  ...TRANSFORM_NODES,
  ...GAMEOBJECT_NODES,
  ...INPUT_ADVANCED_NODES,
  ...SPRITE_NODES,
  ...PHYSICS_NODES,
  ...PHYSICS_ADVANCED_NODES,
  ...AUDIO_NODES,
  ...ZONES_NODES,
  ...LIGHTING_NODES,
  ...CAMERA_NODES,
  ...VFX_NODES,
  ...UTILITY_NODES,
  ...DEBUG_NODES,
];

// ============================================================================
// Custom type registry
// ============================================================================

const _customTypes = new Map<string, CustomTypeDefinition>();

/** Register a custom port data type. */
export function registerCustomType(def: CustomTypeDefinition): void {
  _customTypes.set(def.id, def);
}

/** Get a custom type definition by ID. */
export function getCustomType(id: string): CustomTypeDefinition | undefined {
  return _customTypes.get(id);
}

/** Get all custom type definitions. */
export function getAllCustomTypes(): readonly CustomTypeDefinition[] {
  return [..._customTypes.values()];
}

/** Check if a type is a registered custom type. */
export function isCustomType(typeId: string): boolean {
  return _customTypes.has(typeId);
}

// ============================================================================
// Built-in custom types
// ============================================================================

/** Register engine-specific custom types */
export function registerBuiltinCustomTypes(): void {
  registerCustomType({
    id: 'texture_handle',
    name: 'Texture Handle',
    color: '#ff6b6b',
    defaultValue: null,
    toLiteral: (v) => v != null ? String(v) : 'null',
    fromLiteral: (s) => s === 'null' ? null : s,
  });

  registerCustomType({
    id: 'audio_clip',
    name: 'Audio Clip',
    color: '#f39c12',
    defaultValue: null,
    toLiteral: (v) => v != null ? String(v) : 'null',
    fromLiteral: (s) => s === 'null' ? null : s,
  });

  registerCustomType({
    id: 'prefab_ref',
    name: 'Prefab Reference',
    color: '#74b9ff',
    defaultValue: null,
    toLiteral: (v) => v != null ? String(v) : 'null',
    fromLiteral: (s) => s === 'null' ? null : s,
  });

  registerCustomType({
    id: 'atlas_ref',
    name: 'Atlas Reference',
    color: '#55efc4',
    defaultValue: null,
    toLiteral: (v) => v != null ? String(v) : 'null',
    fromLiteral: (s) => s === 'null' ? null : s,
  });

  registerCustomType({
    id: 'material_ref',
    name: 'Material',
    color: '#a29bfe',
    defaultValue: null,
  });

  // ── Component reference types ──
  // Register a ref:<typeId> custom type for each known component.
  // At runtime the value is an entity ID (i32), but the type encodes
  // which component the referenced game object must carry.
  for (const [typeId, meta] of decoratorRegistry) {
    const color = meta.color || '#00bcd4';
    const name = meta.displayName || typeId;

    registerCustomType({
      id: `ref:${typeId}`,
      name,
      color,
      defaultValue: -1,
      coercesTo: ['entity', 'gameobject'],
      toLiteral: (v) => `${Math.trunc(Number(v ?? -1))}`,
      fromLiteral: (s) => parseInt(s, 10) || -1,
    });

    registerTypeMeta({
      type: `ref:${typeId}`,
      name: `Ref<${name}>`,
      canPreview: true,
      canEdit: false,
      editor: 'none',
      defaultValue: -1,
      formatPreview: (v) => (v as number) === -1 ? 'None' : `#${v}`,
      color,
    });
  }
}

// ============================================================================
// Initialization
// ============================================================================

let _initialized = false;

/**
 * Register all engine nodes and custom types into the default registry.
 * Safe to call multiple times.
 */
export function initializeEngineNodes(registry?: NodeRegistry): void {
  if (_initialized) return;
  _initialized = true;

  const reg = registry ?? getDefaultRegistry();
  reg.registerAll(STANDARD_NODES);
  reg.registerAll(ENGINE_NODES);
  registerBuiltinCustomTypes();

  const total = STANDARD_NODES.length + ENGINE_NODES.length;
  console.log(`[Core] Registered ${total} nodes (${STANDARD_NODES.length} standard + ${ENGINE_NODES.length} engine), ${_customTypes.size} custom types`);
}

/**
 * Reset initialization state (for testing).
 */
export function resetEngineNodes(): void {
  _initialized = false;
  _customTypes.clear();
}
